import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns/format';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { handleExternalUrlClick } from '@/utils/platform';

import { subscriptionApis } from './subscription.query';

export type ActivePlanCardProps = {
  planDisplayName: string;
  billingInterval: 'Annual' | 'Monthly';
  nextBillingDate: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  cancelAtPeriodEnd: boolean;
  noCardTrial?: { endsOn: string; daysLeft: number } | null;
  onCancel: () => void;
  onManage: () => void;
  onAddCard?: () => void;
  isCancelling?: boolean;
  isManaging?: boolean;
  isDisabled?: boolean;
};

export function ActivePlanCard({
  planDisplayName,
  billingInterval,
  nextBillingDate,
  paymentMethod,
  cancelAtPeriodEnd,
  noCardTrial = null,
  onCancel,
  onManage,
  onAddCard,
  isCancelling = false,
  isManaging = false,
  isDisabled = false,
}: ActivePlanCardProps) {
  return (
    <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl py-4 px-6 gap-4">
      <YStack>
        <h3 className="text-lg font-medium text-foreground">
          Littlebird {planDisplayName} ({billingInterval})
        </h3>
        <p className="text-sm text-foreground-muted">
          You have full access to all features
        </p>
      </YStack>

      <XStack className="flex-wrap items-center gap-2">
        {noCardTrial && (
          <XStack className="items-center gap-1.5 px-2.5 py-1 border border-border-strong rounded-sm text-sm text-foreground">
            <Icons.calendar className="w-3.5 h-3.5" />
            <span>
              {noCardTrial.daysLeft === 0
                ? 'Plus trial · ends today'
                : `Plus trial · ends ${noCardTrial.endsOn} · ${noCardTrial.daysLeft} ${noCardTrial.daysLeft === 1 ? 'day' : 'days'} left`}
            </span>
          </XStack>
        )}
        {noCardTrial && (
          <p className="text-sm text-foreground-muted">
            No card on file. Plus ends when the trial does.
          </p>
        )}
        {!noCardTrial && nextBillingDate && (
          <XStack className="items-center gap-1.5 px-2.5 py-1 border border-border-strong rounded-sm text-sm text-foreground">
            <Icons.calendar className="w-3.5 h-3.5" />
            <span>
              {cancelAtPeriodEnd ? 'Ends' : 'Renews'} {nextBillingDate}
            </span>
          </XStack>
        )}
        {paymentMethod && (
          <XStack className="items-center gap-1.5 px-2.5 py-1 border border-border-strong rounded-sm text-sm text-foreground">
            <Icons.creditCard className="w-3.5 h-3.5" />
            <span>
              {paymentMethod.brand} ••••{paymentMethod.last4}
            </span>
          </XStack>
        )}
      </XStack>

      <InvoiceHistory />

      <XStack className="gap-3 justify-end pt-4 border-t border-border">
        {!cancelAtPeriodEnd && !noCardTrial && (
          <Button
            variant="destructive-ghost"
            onClick={onCancel}
            isLoading={isCancelling}
            disabled={isDisabled}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onManage}
          isLoading={isManaging}
          disabled={isDisabled}
        >
          Manage Subscription
        </Button>
        {noCardTrial && (
          <Button
            onClick={onAddCard}
            isLoading={isManaging}
            disabled={isDisabled}
          >
            Add a card to keep Plus
          </Button>
        )}
      </XStack>
    </YStack>
  );
}

export type UpgradePromptCardProps = {
  onUpgrade: () => void;
  isUpgrading?: boolean;
  isDisabled?: boolean;
};

export function UpgradePromptCard({
  onUpgrade,
  isUpgrading = false,
  isDisabled = false,
}: UpgradePromptCardProps) {
  return (
    <XStack className="items-center justify-between px-5 py-4 rounded-xl bg-surface border border-border dark:border-border-strong">
      <YStack className="gap-1">
        <span className="text-sm font-medium text-foreground">
          Get more out of Littlebird
        </span>
        <span className="text-sm text-foreground-muted">
          Upgrade for higher daily limits and more
        </span>
      </YStack>
      <Button
        size="sm"
        onClick={onUpgrade}
        isLoading={isUpgrading}
        disabled={isDisabled}
        className="shrink-0 ml-4"
      >
        Upgrade
      </Button>
    </XStack>
  );
}

function formatAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
}

function InvoiceHistory() {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: subscriptionApis.getInvoices,
  });

  if (isLoading) {
    return (
      <YStack className="py-3 border-t border-border">
        <span className="text-sm text-foreground-muted">
          Loading invoices...
        </span>
      </YStack>
    );
  }

  if (invoices.length === 0) {
    return null;
  }

  return (
    <YStack className="py-3 gap-2 border-t border-border">
      <span className="text-sm font-medium text-foreground">Invoices</span>
      <YStack className="gap-1 max-h-64 overflow-y-auto">
        {invoices.map((invoice) => (
          <XStack
            key={invoice.id}
            className="items-center justify-between py-2 px-3 rounded-md hover:bg-secondary/50"
          >
            <XStack className="items-center gap-3">
              <span className="text-sm text-foreground">
                {format(new Date(invoice.date * 1000), 'MMM d, yyyy')}
              </span>
              <span className="text-sm text-foreground-muted">
                {formatAmount(invoice.amount_paid, invoice.currency)}
              </span>
              {invoice.number && (
                <span className="text-xs text-foreground-muted">
                  #{invoice.number}
                </span>
              )}
            </XStack>
            {invoice.hosted_invoice_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  handleExternalUrlClick(invoice.hosted_invoice_url!)
                }
                className="h-7 text-xs"
              >
                View
                <Icons.externalLink className="w-3 h-3 ml-1" />
              </Button>
            )}
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}
